import { useState, useRef, useEffect, useCallback } from 'react';
import { Pencil, Trash2, GripVertical, Check, X } from 'lucide-react';
import type { StickyNote, StickyNoteColor } from '@/hooks/use-staffing-board-notes';
import { formatDateTimeInTimeZone } from '@/lib/date-utils';

const COLOR_CLASSES: Record<StickyNoteColor, { bg: string; border: string; ring: string }> = {
  yellow: { bg: 'bg-yellow-100', border: 'border-yellow-300', ring: 'ring-yellow-400' },
  blue: { bg: 'bg-blue-100', border: 'border-blue-300', ring: 'ring-blue-400' },
  green: { bg: 'bg-green-100', border: 'border-green-300', ring: 'ring-green-400' },
  pink: { bg: 'bg-pink-100', border: 'border-pink-300', ring: 'ring-pink-400' },
  orange: { bg: 'bg-orange-100', border: 'border-orange-300', ring: 'ring-orange-400' },
  purple: { bg: 'bg-purple-100', border: 'border-purple-300', ring: 'ring-purple-400' },
};

const COLOR_DOT_CLASSES: Record<StickyNoteColor, string> = {
  yellow: 'bg-yellow-400',
  blue: 'bg-blue-400',
  green: 'bg-green-400',
  pink: 'bg-pink-400',
  orange: 'bg-orange-400',
  purple: 'bg-purple-400',
};

const ALL_COLORS: StickyNoteColor[] = ['yellow', 'blue', 'green', 'pink', 'orange', 'purple'];

interface StickyNoteCardProps {
  note: StickyNote;
  canEdit: boolean;
  onUpdate: (payload: { id: number; content?: string; color?: StickyNoteColor; position_x?: number; position_y?: number }) => void;
  onDelete: (id: number) => void;
  onMoveStart?: (id: number, e: React.PointerEvent) => void;
  timeZone?: string;
}

export function StickyNoteCard({ note, canEdit, onUpdate, onDelete, onMoveStart, timeZone = 'UTC' }: StickyNoteCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(note.content);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const colorPickerRef = useRef<HTMLDivElement>(null);

  const colors = COLOR_CLASSES[note.color];

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.selectionStart = textareaRef.current.value.length;
    }
  }, [isEditing]);

  useEffect(() => {
    if (!showColorPicker) return;
    function handleClickOutside(e: Event) {
      if (colorPickerRef.current && !colorPickerRef.current.contains(e.target as Node)) {
        setShowColorPicker(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showColorPicker]);

  const handleSave = useCallback(() => {
    const trimmed = editContent.trim();
    if (trimmed && trimmed !== note.content) {
      onUpdate({ id: note.id, content: trimmed });
    }
    setIsEditing(false);
  }, [editContent, note.content, note.id, onUpdate]);

  const handleCancel = useCallback(() => {
    setEditContent(note.content);
    setIsEditing(false);
  }, [note.content]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSave();
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  }, [handleSave, handleCancel]);

  const handleColorChange = useCallback((color: StickyNoteColor) => {
    onUpdate({ id: note.id, color });
    setShowColorPicker(false);
  }, [note.id, onUpdate]);

  const handleDelete = useCallback(() => {
    if (confirmDelete) {
      onDelete(note.id);
      setConfirmDelete(false);
    } else {
      setConfirmDelete(true);
      setTimeout(() => setConfirmDelete(false), 3000);
    }
  }, [confirmDelete, note.id, onDelete]);

  return (
    <div
      data-testid={`sticky-note-${note.id}`}
      className={`group relative rounded-lg border ${colors.bg} ${colors.border} shadow-sm transition-shadow hover:shadow-md`}
      style={{ minWidth: 180, maxWidth: 220, width: '100%', minHeight: 80 }}
    >
      {/* Drag handle */}
      {canEdit && (
        <div
          className="absolute top-1 left-1 cursor-grab opacity-60 md:opacity-0 md:group-hover:opacity-60 transition-opacity touch-none"
          onPointerDown={(e) => onMoveStart?.(note.id, e)}
          data-testid={`sticky-note-drag-${note.id}`}
        >
          <GripVertical className="h-4 w-4 text-gray-500" />
        </div>
      )}

      {/* Action buttons (top-right) */}
      {canEdit && !isEditing && (
        <div className="absolute top-1 right-1 flex items-center gap-1 md:gap-0.5 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
          <button
            onClick={() => setShowColorPicker((v) => !v)}
            className="p-1.5 md:p-1 rounded hover:bg-black/10 transition-colors"
            title="Change color"
            data-testid={`sticky-note-color-btn-${note.id}`}
          >
            <div className={`h-4 w-4 md:h-3 md:w-3 rounded-full ${COLOR_DOT_CLASSES[note.color]} border border-black/20`} />
          </button>
          <button
            onClick={() => { setEditContent(note.content); setIsEditing(true); }}
            className="p-1.5 md:p-1 rounded hover:bg-black/10 transition-colors"
            title="Edit note"
            data-testid={`sticky-note-edit-btn-${note.id}`}
          >
            <Pencil className="h-4 w-4 md:h-3 md:w-3 text-gray-600" />
          </button>
          <button
            onClick={handleDelete}
            className={`p-1.5 md:p-1 rounded transition-colors ${confirmDelete ? 'bg-red-200 hover:bg-red-300' : 'hover:bg-black/10'}`}
            title={confirmDelete ? 'Click again to confirm' : 'Delete note'}
            data-testid={`sticky-note-delete-btn-${note.id}`}
          >
            <Trash2 className={`h-4 w-4 md:h-3 md:w-3 ${confirmDelete ? 'text-red-700' : 'text-gray-600'}`} />
          </button>
        </div>
      )}

      {/* Color picker dropdown */}
      {showColorPicker && (
        <div
          ref={colorPickerRef}
          className="absolute top-7 right-1 z-10 flex gap-1 rounded-lg border border-gray-200 bg-white p-1.5 shadow-lg"
          data-testid={`sticky-note-color-picker-${note.id}`}
        >
          {ALL_COLORS.map((c) => (
            <button
              key={c}
              onClick={() => handleColorChange(c)}
              className={`h-7 w-7 md:h-5 md:w-5 rounded-full ${COLOR_DOT_CLASSES[c]} border-2 transition-transform hover:scale-110 ${
                c === note.color ? 'border-gray-800 scale-110' : 'border-transparent'
              }`}
              title={c}
              data-testid={`sticky-note-color-${c}`}
            />
          ))}
        </div>
      )}

      {/* Content */}
      <div className="px-3 py-2 pt-3">
        {isEditing ? (
          <div>
            <textarea
              ref={textareaRef}
              value={editContent}
              onChange={(e) => setEditContent(e.target.value.slice(0, 500))}
              onKeyDown={handleKeyDown}
              className={`w-full resize-none rounded border-0 bg-transparent text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 ${colors.ring}`}
              rows={3}
              maxLength={500}
              placeholder="Type your note..."
              data-testid={`sticky-note-textarea-${note.id}`}
            />
            <div className="mt-1 flex items-center justify-between">
              <span className="text-[10px] text-gray-400">{editContent.length}/500</span>
              <div className="flex items-center gap-1">
                <button
                  onClick={handleCancel}
                  className="p-1 rounded hover:bg-black/10"
                  title="Cancel (Esc)"
                  data-testid={`sticky-note-cancel-${note.id}`}
                >
                  <X className="h-3.5 w-3.5 text-gray-500" />
                </button>
                <button
                  onClick={handleSave}
                  className="p-1 rounded hover:bg-black/10"
                  title="Save (Ctrl+Enter)"
                  data-testid={`sticky-note-save-${note.id}`}
                >
                  <Check className="h-3.5 w-3.5 text-green-600" />
                </button>
              </div>
            </div>
          </div>
        ) : (
          <p
            className="text-sm text-gray-800 whitespace-pre-wrap break-words leading-snug"
            onDoubleClick={() => {
              if (canEdit) {
                setEditContent(note.content);
                setIsEditing(true);
              }
            }}
            data-testid={`sticky-note-content-${note.id}`}
          >
            {note.content}
          </p>
        )}
      </div>

      {/* Footer — author + time */}
      {!isEditing && (
        <div className="px-3 pb-1.5">
          <p className="text-[10px] text-gray-400 truncate">
            {note.created_by_username ?? 'Unknown'}
            {' \u00b7 '}
            {formatDateTimeInTimeZone(note.updated_at, timeZone, {
              month: 'short',
              day: 'numeric',
            })}
          </p>
        </div>
      )}
    </div>
  );
}
